"use client";
import { Button } from "@/components/ui/button";
import { Menu, User } from "lucide-react";
import Image from "next/image";
import { useState } from "react";

import { useRouter } from "next/navigation";
import "./header.css";

import { images } from "@/public";
import { useAppSelector } from "@/src/store/hooks";
import { routes, Theme } from "@/src/utilities";
import { usePathname } from "next/navigation"; // App Router
import UniversalSearch from "../../custom/UniversalSearch";
import { AuthButton } from "./AuthButton";
import MegaMenu from "./MegaMenu";
import { useScroll, useMotionValueEvent } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Link from "next/link";

const THEME = process.env.NEXT_PUBLIC_THEME as Theme || "orange";
/**
 *
 * @returns Header component
 * @description Header component is a component that is used to display the header of the website based on the theme
 * The THEME is a type-safe env variable that determines the theme of the website
 * the MegaMenu component is used to display the nested menu of the website based on the theme
 * the SearchUniversal component is used to display the search bar of the website based on the theme its not to be displayed on the home page
 */
const Header = () => {
  const { productSearch: productList } = useAppSelector(
    (root) => root.unGatedContentStateReducer
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSearchOnHome, setShowSearchOnHome] = useState(false);
  const router = useRouter();

  const pathname = usePathname();
  const { scrollY } = useScroll();

  // Track scroll position and show search on home page after 100vh
  useMotionValueEvent(scrollY, "change", (latest) => {
    if (pathname === "/") {
      const viewportHeight =
        typeof window !== "undefined" ? window.innerHeight : 0;
      setShowSearchOnHome(latest > viewportHeight);
    }
  });

  // 'active' is the currently highlighted left-category (fixes your "active is not defined" error)
  console.log(productList, 'productList');

  return (
    <header
      className={`${THEME === "orange"
        ? "bg-orange-950"
        : "lg:bg-theme-secondary bg-[#3F160A]"
        } sticky not-first:top-0 z-50 shadow-md w-full`}
    >
      {/* Top Bar */}
      <div
        className={`text-white py-2 px-4 flex justify-between w-full max-w-7xl mx-auto`}
      >
        <Link href="/" className="flex items-center">
          <Image
            src={images.logo}
            className="w-20 lg:w-32 h-12 cursor-pointer"
            alt="Logo"
            width={200}
            height={200}

          />
        </Link>
        <div className="flex gap-4">
          {(pathname !== "/" || showSearchOnHome) && (
            <div className="w-full hidden lg:flex">
              <div className="flex items-center gap-4 lg:min-w-[320px] min-w-[240px]">
                {/* {productList?.length > 0 && ( */}
                <UniversalSearch variant="simple" />
                {/* )} */}
              </div>
            </div>
          )}
          <div className="hidden lg:flex items-center gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                className="text-white font-semibold hover:bg-white/10 hover:text-primary"
                onClick={() =>
                  window.scrollTo({
                    top: document.getElementById("footer")?.offsetTop,
                    behavior: "smooth",
                  })
                }
              >
                Subscribe
              </Button>
              <AuthButton />
            </div>
          </div>
          {/* Mobile Menu */}
          <div className="lg:hidden flex items-center gap-6">
            <DropdownMenu>
              <DropdownMenuTrigger className="lg:hidden border-none outline-none">
                <User className="h-5 w-5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="lg:hidden border-none outline-none "
                align="end"
              >
                <DropdownMenuItem
                  onClick={() => {
                    window.scrollTo({
                      top: document.getElementById("footer")?.offsetTop,
                      behavior: "smooth",
                    });
                  }}
                >
                  Subscribe
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => window.open(`/${routes.LOGIN}`, "_blank")}
                >
                  Login
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <DropdownMenuTrigger className="lg:hidden border-none outline-none">
                <Menu className="h-5 w-5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="lg:hidden border-none outline-none "
                align="end"
              >
                <div className="lg:hidden py-4 w-[90vw] bg-white backdrop-blur-md h-[80vh] overflow-y-auto">
                  <div className="flex flex-col space-y-4">
                    <div className="relative px-4">
                      {/* {productList?.length > 0 && ( */}
                      <UniversalSearch
                        variant="simple"
                        onSelectResult={() => setIsMenuOpen(false)}
                      />
                      {/* )} */}
                    </div>
                    <MegaMenu mobile={true} setIsMenuOpen={setIsMenuOpen} />
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="absolute bg-white/85 backdrop-blur-2xl flex justify-between mx-auto px-4 w-full">
        <div className="hidden lg:flex items-center justify-between h-12 mx-auto">
          {/* Desktop Navigation */}
          <MegaMenu />
          {/* Search and Actions */}
        </div>
      </div>
    </header>
  );
};

export default Header;
